import { provideHttpClient } from '@angular/common/http';
import {
  type ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  withNavigationErrorHandler,
} from '@angular/router';
import {
  MCP_PROXY_CLIENT_CONFIG,
  type McpProxyClientConfig,
  provideMcpTokenRefreshProxy,
} from '@bizzdesign/ai-assistant/mcp-client-auth';
import {
  AI_ASSISTANT_CONFIG,
  type AiAssistantConfig,
} from '@bizzdesign/ai-assistant/shared';
import { SessionService } from '@bizzdesign/auth';
import {
  type AiWorkflowsConfig,
  BizzdesignSdk,
  type Config,
  type GraphQLCacheConfig,
  type LogLevel,
  type TokenTypes,
} from '@bizzdesign/bizzdesign-sdk';
import { sdkFactory } from '@bizzdesign/bizzdesign-sdk/browser';
import { IMPORT_CONFIG } from '@bizzdesign/feature-data-management';
import {
  COLLAB_SERVER_CONFIG,
  type CollabServerConfig,
} from '@bizzdesign/models-collab-abstract';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';

import {
  POSTHOG_CONFIG_TOKEN,
  type PosthogConfig,
} from '@bizzdesign/plugins/posthog';

import { AuthClient, AuthTokenProvider } from '@bizzdesign/sdk-auth';
import { DeliverableClient } from '@bizzdesign/sdk-deliverable';
import { ImportClient } from '@bizzdesign/sdk-import';
import { PopulationClient } from '@bizzdesign/sdk-population';
import { SearchClient } from '@bizzdesign/sdk-search';
import { VersionClient } from '@bizzdesign/sdk-version';
import { WorkflowClient } from '@bizzdesign/sdk-workflow';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  InvitationAnalyticsService,
  KnowledgeGraphClientArchiMate,
  KnowledgeGraphClientBDCore,
  LOG_MODE_CONFIG,
  OsDetectorService,
  ProductAnalyticsService,
} from '@bizzdesign/shared/services';
import {
  baseRoutes,
  DEFAULT_ONBOARDING_FLOW_ID,
  FEATURE_FLAGS_CONFIG,
  type FeatureFlagsConfig,
  highchartsConfig,
  UNIFY_MANAGEMENT_URL,
  USERFLOW_CONFIG,
  type UserflowConfig,
  VERSION_INFO,
  type VersionInfo,
  VISUALIZATION_TEMPLATE_REGISTER,
} from '@bizzdesign/shared/utils';
import { visualizationTemplateRegister } from '@bizzdesign/shared/visualization-templates';
import { provideTheme } from '@bizzdesign/styles';
import { WORKSPACES_SHARED_PROVIDERS } from '@bizzdesign/workspaces/feature-common/providers';
import * as Sentry from '@sentry/angular';
import { provideHighcharts } from 'highcharts-angular';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import * as versionInfoJson from '../environments/_info.json';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { ConfigService } from './config.service';
import { CombinedErrorHandler } from './error.handler';
import { handleNavigationError } from './navigation-error.utils';

type EnvironmentType = 'e2e' | 'local' | 'development' | 'production';

export type Environment = {
  production: boolean;
  type: EnvironmentType;
  // Sentry is otherwise only active when `production` is true. Set this to `true` in your
  // personal `environment.development.ts` to manually test Sentry (errors/replay) against localhost.
  enableSentryLocally?: boolean;
  cognitoRegion: string; // eu-west-1
  cognitoDomain: string; // XYZ.eu-west-1.amazoncognito.com'
  cognitoCallbackUrl: string; // http://localhost:4200
  cognitoUserPoolId: string; // User pool ID found on main Cognito page
  cognitoClientId: string; // Found in 'App integration' tab of the user pool page
  configUrl: string; // The URL for the config server
  appSyncUrl: string; // https://XYZ.eu-west-1.amazonaws.com/graphql,
  appSyncRealtimeUrl: string; // wss://XYZ.appsync-realtime-api.eu-west-1.amazonaws.com/graphql
  minimumLogLevel: LogLevel;
  useTokenType: TokenTypes;
  queryBatchEnabled: boolean;
  enableCache?: GraphQLCacheConfig;
  queryBatchMaxRequests: number;
  queryBatchInterval: number;
  batchImport: {
    parallelRequests: number;
    batchSize: number;
  };
  importNuvoDeveloperMode: boolean;
  importNuvoLicenseKey: string;
  collaborationServers: CollabServerConfig;
  featureFlags: FeatureFlagsConfig;
  aiAssistant: AiAssistantConfig;
  posthogConfig: PosthogConfig | undefined;
  aiWorkflowsConfig: AiWorkflowsConfig | undefined;
  userflow: UserflowConfig;
  // Base URL of the Unify Management app, or '' when it is not deployed for this environment.
  unifyManagementUrl?: string;
};

/**
 * Prefer the browser's current origin so the same build works regardless of which
 * port the dev server bound to (e.g. 4200-4205); each origin must still be
 * allowlisted in the Cognito app client's callback URLs. Falls back to the
 * configured `cognitoCallbackUrl` when there is no `window` (SSR, tests).
 */
function resolveCognitoCallbackUrl(): string {
  return globalThis.location?.origin ?? environment.cognitoCallbackUrl;
}

export const sdkConfig: Config = {
  graphqlEndpoint: environment.appSyncUrl,
  graphqlRealtimeEndpoint: environment.appSyncRealtimeUrl,
  authentication: {
    type: 'cognito-user',
    cognito: {
      region: environment.cognitoRegion,
      endpoint: environment.cognitoDomain,
      userPoolId: environment.cognitoUserPoolId,
      callbackUrl: resolveCognitoCallbackUrl(),
      clientId: environment.cognitoClientId,
    },
  },
  logSettings: {
    minimumLogLevel: environment.minimumLogLevel,
  },
  useTokenType: environment.useTokenType,
  queryBatching: {
    enabled: environment.queryBatchEnabled,
    batchMaxRequests: environment.queryBatchMaxRequests,
    batchInterval: environment.queryBatchInterval,
  },
  importSettings: {
    parallelRequests: environment.batchImport.parallelRequests,
    batchSize: environment.batchImport.batchSize,
  },
  aiWorkflowsConfig: environment.aiWorkflowsConfig
    ? {
        baseUrl: environment.aiWorkflowsConfig.baseUrl,
      }
    : undefined,
  enableCache: environment.enableCache,
};

const featureFlagsConfig: FeatureFlagsConfig = {
  aiSearchEnabled: environment.featureFlags.aiSearchEnabled,
  manageAttributesEnabled: environment.featureFlags.manageAttributesEnabled,
  integrationsEnabled: environment.featureFlags.integrationsEnabled,
  populationsEnabled: environment.featureFlags.populationsEnabled,
  customTemplatesEnabled: environment.featureFlags.customTemplatesEnabled,
  workspaceOverviewEnabled: environment.featureFlags.workspaceOverviewEnabled,
  dataImportV2Enabled: environment.featureFlags.dataImportV2Enabled,
  addToFrameWithoutSelectionEnabled:
    environment.featureFlags.addToFrameWithoutSelectionEnabled,
  fromRepoDeletionEnabled: environment.featureFlags.fromRepoDeletionEnabled,
  addViewToFrameEnabled: environment.featureFlags.addViewToFrameEnabled,
  publishingFlowV2Enabled: environment.featureFlags.publishingFlowV2Enabled,
  aiSearchCypherQueryEnabled:
    environment.featureFlags.aiSearchCypherQueryEnabled,
  imagesOnFramesEnabled: environment.featureFlags.imagesOnFramesEnabled,
  workspacesCacheEnabled: environment.featureFlags.workspacesCacheEnabled,
  workspacesCachePayload: environment.featureFlags.workspacesCachePayload,
  designAssistantModelerSDKLayoutEnabled:
    environment.featureFlags.designAssistantModelerSDKLayoutEnabled,
  skillManagementEnabled: environment.featureFlags.skillManagementEnabled,
  trialExperienceEnabled: environment.featureFlags.trialExperienceEnabled,
  aiAssistantFeedbackV2: environment.featureFlags.aiAssistantFeedbackV2,
  enableDesignAssistantInsightSuggestions:
    environment.featureFlags.enableDesignAssistantInsightSuggestions,
  aiAssistantFileUploadEnabled:
    environment.featureFlags.aiAssistantFileUploadEnabled,
  aiAssistantDeliverablePersistence:
    environment.featureFlags.aiAssistantDeliverablePersistence,
  aiAssistantUnifiedEnabled: environment.featureFlags.aiAssistantUnifiedEnabled,
  iconLeftPlacementEnabled: environment.featureFlags.iconLeftPlacementEnabled,
  verticalTextAlignmentEnabled:
    environment.featureFlags.verticalTextAlignmentEnabled,
  objectSearchResultsTableEnabled:
    environment.featureFlags.objectSearchResultsTableEnabled,
  devClippyEnabled: environment.featureFlags.devClippyEnabled,
  frameCommentsEnabled: environment.featureFlags.frameCommentsEnabled,
  createWorkspaceWithSourcesEnabled:
    environment.featureFlags.createWorkspaceWithSourcesEnabled,
};

const aiAssistantConfig: AiAssistantConfig = {
  triggerWorkflowEndpoint: environment.aiAssistant.triggerWorkflowEndpoint,
  checkWorkflowStatusEndpoint:
    environment.aiAssistant.checkWorkflowStatusEndpoint,
  chatEndpoint: environment.aiAssistant.chatEndpoint,
};

const versionInfo: VersionInfo = {
  id: versionInfoJson.id,
  version: versionInfoJson.version,
};

export type AuthenticatedConfig = {
  importNuvoLicenseKey: string;
  importNuvoDeveloperMode: boolean;
};

const userflowConfig: UserflowConfig = {
  userflowKey: environment.userflow.userflowKey,
  customerName: environment.userflow.customerName,
  onboardingFlowId:
    environment.userflow.onboardingFlowId ?? DEFAULT_ONBOARDING_FLOW_ID,
};

const sdk: BizzdesignSdk = sdkFactory(sdkConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom([BrowserAnimationsModule, SharedI18nModule]),
    provideRouter(
      appRoutes,
      withNavigationErrorHandler((error) =>
        handleNavigationError(error, baseRoutes.pageNotFound),
      ),
    ),
    MessageService,
    {
      provide: BizzdesignSdk,
      useFactory: () => sdk,
    },
    {
      provide: PopulationClient,
      useFactory: () => sdk.populationClient,
    },
    {
      provide: ImportClient,
      useFactory: () => sdk.importClient,
    },
    {
      provide: DeliverableClient,
      useFactory: () => sdk.deliverableClient,
    },
    {
      provide: AuthTokenProvider,
      useFactory: () => sdk.authTokenProvider,
    },
    {
      // Browser-side OAuth-DCR routes calls through our /v1/mcp/oauth/*
      // proxy so CSP `connect-src` doesn't have to list MCP server
      // origins. The base URL strips the `/ai` suffix from the
      // assistant baseUrl because MCP routes live at the API root.
      provide: MCP_PROXY_CLIENT_CONFIG,
      useFactory: (): McpProxyClientConfig => ({
        apiBaseUrl: stripAiSuffix(environment.aiWorkflowsConfig?.baseUrl ?? ''),
        getIdToken: () => sdk.authTokenProvider.getIdToken(),
      }),
    },
    // Binds `MCP_TOKEN_REFRESH_PROXY` to the same `McpProxyClient` instance
    // so the token store can refresh `oauth-authorization-code` access
    // tokens via the proxy (which injects the server-stored client_secret).
    provideMcpTokenRefreshProxy(),
    {
      provide: AuthClient,
      useFactory: () => sdk.authClient,
    },
    {
      provide: WorkflowClient,
      useFactory: () => sdk.workflowClient,
    },
    {
      provide: VersionClient,
      useFactory: () => sdk.versionClient,
    },
    {
      provide: SearchClient,
      useFactory: () => sdk.searchClient,
    },
    {
      provide: KnowledgeGraphClientBDCore,
      useFactory: () => sdk.knowledgeGraphClient('BDCore'),
    },
    {
      provide: KnowledgeGraphClientArchiMate,
      useFactory: () => sdk.knowledgeGraphClient('ArchiMate'),
    },
    provideHttpClient(),
    provideAppInitializer(async () => {
      inject(Sentry.TraceService);
      const configService = inject(ConfigService);
      const sessionService = inject(SessionService);
      const productAnalyticsService = inject(ProductAnalyticsService);
      const invitationAnalyticsService = inject(InvitationAnalyticsService);
      const osDetectorService = inject(OsDetectorService);
      const featureFlagsStore = inject(FeatureFlagsStore);

      const invitationPayload =
        invitationAnalyticsService.parseAndStoreInvitationParam();
      if (invitationPayload) {
        invitationAnalyticsService.trackCtaClicked(invitationPayload);
      }

      await configService.loadAuthenticatedConfig();

      const isLoggedIn = await firstValueFrom(
        sessionService.getAuthenticatedUser(),
      );

      if (isLoggedIn) {
        const identitySent = await productAnalyticsService.sendIdentity();
        productAnalyticsService.sendEvent('app_loaded');
        if (identitySent) {
          await featureFlagsStore.reloadFlags();
        }
      }
      await osDetectorService.loadLayoutMap();
    }),
    {
      provide: POSTHOG_CONFIG_TOKEN,
      useValue: {
        trackingEnabled: environment.posthogConfig?.trackingEnabled,
        group: environment.posthogConfig?.group,
        tenantId: environment.posthogConfig?.tenantId,
        posthogUiHost: environment.posthogConfig?.posthogUiHost,
        posthogApiHost: environment.posthogConfig?.posthogApiHost,
        posthogApiKey: environment.posthogConfig?.posthogApiKey,
        bootstrapFlags: featureFlagsConfig,
      },
    },
    {
      provide: IMPORT_CONFIG,
      useFactory: (configService: ConfigService) => configService.importConfig,
      deps: [ConfigService],
    },
    {
      provide: ErrorHandler,
      useValue: new CombinedErrorHandler(Sentry.createErrorHandler()),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: COLLAB_SERVER_CONFIG,
      useValue: environment.collaborationServers,
    },
    provideAnimationsAsync(),
    provideTheme(),
    {
      provide: VISUALIZATION_TEMPLATE_REGISTER,
      useValue: visualizationTemplateRegister,
    },
    { provide: FEATURE_FLAGS_CONFIG, useValue: featureFlagsConfig },
    {
      provide: AI_ASSISTANT_CONFIG,
      useValue: aiAssistantConfig,
    },
    {
      provide: USERFLOW_CONFIG,
      useValue: userflowConfig,
    },
    {
      provide: UNIFY_MANAGEMENT_URL,
      useValue: environment.unifyManagementUrl ?? '',
    },
    {
      provide: VERSION_INFO,
      useValue: versionInfo,
    },
    {
      provide: LOG_MODE_CONFIG,
      useValue: environment.production ? 'production' : 'development',
    },
    ...WORKSPACES_SHARED_PROVIDERS,
    provideHighcharts(highchartsConfig),
  ],
};

/**
 * The AI assistant lambdas live at `<host>/ai/...` but the MCP routes
 * live at `<host>/v1/mcp/...`. Both share an API gateway, so we derive
 * the API root from the assistant baseUrl by stripping the `/ai` suffix.
 */
function stripAiSuffix(baseUrl: string): string {
  return baseUrl.replace(/\/ai\/?$/, '');
}
